import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUpload, FiVideo, FiDownload, FiRefreshCw, FiLoader } from 'react-icons/fi';
import { BsFiletypeGif } from 'react-icons/bs';
import SEO from '../components/SEO';
import { videoToGifSchemas } from '../schemas';
import API_BASE_URL from '../config';
import './VideoToGIF.css';

const VideoToGIF = () => {
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const [gifUrl, setGifUrl] = useState(null);
  const [error, setError] = useState('');
  const [videoDuration, setVideoDuration] = useState(0);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    return () => {
      if (videoPreview) URL.revokeObjectURL(videoPreview);
    };
  }, [videoPreview]);

  useEffect(() => {
    return () => {
      if (gifUrl) URL.revokeObjectURL(gifUrl);
    };
  }, [gifUrl]);

  const MAX_DURATION = 15; // 15 seconds limit

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateAndSetVideo(file);
    }
  };

  const validateAndSetVideo = (file) => {
    // Check if it's a video file
    if (!file.type.startsWith('video/')) {
      setError('Please upload a valid video file');
      return;
    }

    // Check file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      setError('Video file size should be less than 50MB');
      return;
    }

    setError('');
    const url = URL.createObjectURL(file);
    setVideoPreview(url);
    setVideoFile(file);
    setGifUrl(null);

    // Check video duration
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const duration = video.duration;
      setVideoDuration(duration);
      
      if (duration > MAX_DURATION) {
        setError(`Video is ${duration.toFixed(1)}s long. Please upload a video up to ${MAX_DURATION} seconds.`);
      }
    };
    video.src = url;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndSetVideo(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const convertToGIF = async () => {
    if (!videoFile || videoDuration > MAX_DURATION) return;

    setIsConverting(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('video', videoFile);
      
      const response = await fetch(`${API_BASE_URL}/api/convert-to-gif`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Conversion failed');
      }

      if (data.success) {
        // Convert base64 to blob URL for preview
        const byteCharacters = atob(data.gif);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/gif' });
        const gifBlobUrl = URL.createObjectURL(blob);
        
        setGifUrl(gifBlobUrl);
      }
      
    } catch (err) {
      setError(err.message || 'Failed to convert video. Please try again.');
      console.error(err);
    } finally {
      setIsConverting(false);
    }
  };

  const downloadGIF = () => {
    if (!gifUrl) return;
    
    // Create download link
    const link = document.createElement('a');
    link.href = gifUrl;
    link.download = `${videoFile.name.split('.')[0]}.gif`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetConverter = () => {
    setVideoFile(null);
    setVideoPreview(null);
    setGifUrl(null);
    setError('');
    setVideoDuration(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="video-to-gif-page">
      <SEO 
        title="Video to GIF Converter Online – Free & Fast"
        description="Convert video clips to high-quality GIF online free. Turn MP4 to GIF instantly for social media, memes & messaging."
        keywords="video to gif, convert mp4 to gif, gif maker online"
        path="/video-to-gif"
        schema={videoToGifSchemas}
      />
      <section className="tool-hero">
        <div className="tool-hero-container">
          <motion.div 
            className="tool-icon-large"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <BsFiletypeGif />
          </motion.div>

          <motion.h1
            className="tool-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Video to GIF Converter
          </motion.h1>

          <motion.p
            className="tool-description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Convert your videos to animated GIF format. Upload videos up to {MAX_DURATION} seconds long.
          </motion.p>
        </div>
      </section>

      <section className="tool-workspace">
        <div className="workspace-container">
          <AnimatePresence mode="wait">
            {!videoFile ? (
              <motion.div
                key="upload"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="upload-zone"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
              >
                <motion.div
                  className="upload-icon"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <FiUpload />
                </motion.div>
                <h3>Upload Your Video</h3>
                <p>Drag and drop or click to browse (Max {MAX_DURATION}s, up to 50MB)</p>
                <p className="supported-formats">Supported: MP4, MOV, AVI, WebM</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </motion.div>
            ) : (
              <motion.div
                key="converter"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="converter-workspace"
              >
                <div className="video-gif-grid">
                  {/* Video Preview */}
                  <motion.div
                    className="preview-card-minimal"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="card-header-small">
                      <FiVideo />
                      <span>Original Video</span>
                    </div>
                    <div className="card-content">
                      <video
                        ref={videoRef}
                        src={videoPreview}
                        controls
                        playsInline
                        preload="auto"
                        className="video-preview"
                        onLoadedMetadata={(e) => {
                          console.log('Video loaded:', e.target.duration);
                        }}
                        onError={(e) => {
                          console.error('Video error:', e);
                          setError('Failed to load video');
                        }}
                      />
                    </div>
                  </motion.div>

                  {/* GIF Preview */}
                  <motion.div
                    className="preview-card-minimal"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <div className="card-header-small">
                      <BsFiletypeGif />
                      <span>GIF Output</span>
                    </div>
                    <div className="card-content">
                      <AnimatePresence mode="wait">
                        {isConverting ? (
                          <motion.div
                            key="converting"
                            className="loading-circle"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            <svg className="circular-loader" viewBox="25 25 50 50">
                              <circle className="loader-path" cx="50" cy="50" r="20" fill="none" strokeWidth="3" />
                            </svg>
                          </motion.div>
                        ) : gifUrl ? (
                          <motion.img
                            key="gif"
                            src={gifUrl}
                            alt="Generated GIF"
                            className="gif-preview-minimal"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                          />
                        ) : (
                          <motion.div
                            key="waiting"
                            className="waiting-minimal"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                          >
                            <BsFiletypeGif />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                </div>

                {/* Error Message */}
                {error && (
                  <motion.div
                    className="error-message"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {error}
                  </motion.div>
                )}

                {/* Action Buttons */}
                <motion.div
                  className="action-buttons-row"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  {!gifUrl ? (
                    <>
                      <motion.button
                        className="btn-primary-large"
                        onClick={convertToGIF}
                        disabled={isConverting || videoDuration > MAX_DURATION}
                        whileHover={{ scale: isConverting ? 1 : 1.05 }}
                        whileTap={{ scale: isConverting ? 1 : 0.95 }}
                      >
                        {isConverting ? (
                          <>
                            <FiLoader className="spinning" />
                            Converting...
                          </>
                        ) : (
                          <>
                            <BsFiletypeGif />
                            Convert to GIF
                          </>
                        )}
                      </motion.button>
                      <motion.button
                        className="btn-secondary"
                        onClick={resetConverter}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FiRefreshCw />
                        Choose Different Video
                      </motion.button>
                    </>
                  ) : (
                    <>
                      <motion.button
                        className="btn-primary-large"
                        onClick={downloadGIF}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring' }}
                      >
                        <FiDownload />
                        Download GIF
                      </motion.button>
                      <motion.button
                        className="btn-secondary"
                        onClick={resetConverter}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FiRefreshCw />
                        Convert Another Video
                      </motion.button>
                    </>
                  )}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Info Section */}
      <section className="info-section">
        <div className="info-container">
          <motion.div
            className="info-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3>Why {MAX_DURATION} seconds?</h3>
            <p>
              Most GIFs are short and sweet! We limit videos to {MAX_DURATION} seconds to ensure:
            </p>
            <ul>
              <li>Smaller file sizes (perfect for sharing)</li>
              <li>Faster conversion times</li>
              <li>Better quality output</li>
              <li>Quick loading on all platforms</li>
            </ul>
          </motion.div>

          <motion.div
            className="info-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h3>Tips for Best Results</h3>
            <ul>
              <li>Use videos with high contrast for better GIF quality</li>
              <li>Keep your video length between 3-10 seconds</li>
              <li>Simple movements convert better than complex scenes</li>
              <li>720p resolution works best for balanced quality and size</li>
            </ul>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default VideoToGIF;
